import { useEffect, useState, useRef, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Card,
  Button,
  Space,
  Tag,
  Descriptions,
  Spin,
  Alert,
  Typography,
  Tabs,
} from 'antd';
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import { mappingApi } from '../services/api/mapping';

import { format } from 'date-fns';
import { MappingVerification } from '../components/verification/MappingVerification';
import { TestPreviewModal } from '../components/authoring/TestPreviewModal';
import { tenantIdFromLabels } from '../services/api/client';

const { Title, Text } = Typography;

interface PdfWithUrl {
  key: string;
  url: string;
}

interface MappingDetailData {
  key: string;
  name: string;
  status: string;
  dateFormat?: string;
  pdfs?: PdfWithUrl[];
  docType?: string;
  labels?: string[];
  creator?: string;
  dateCreated?: string;
  lastUpdated?: string;
  lastUpdatedBy?: string;
  tenantId?: string;
  [key: string]: any; // For dynamic PDF data
}

export function MappingDetail() {
  const { key } = useParams<{ key: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [mapping, setMapping] = useState<MappingDetailData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(
    (location.state as any)?.activeTab || 'details'
  );
  const [selectedPdfHash, setSelectedPdfHash] = useState<string | undefined>(
    undefined
  );
  const [testModalOpen, setTestModalOpen] = useState(false);
  const [tenantId, setTenantId] = useState<string | undefined>(undefined);
  const lastFetchedKey = useRef<string | null>(null);

  // const { user } = useAuthStore();
  const isMapper = true; // todo user?.attributes?.['custom:group'] === 'MAPPER';

  useEffect(() => {
    // Check if we have updated mapping data from navigation state (after verification)
    const updatedMapping = (location.state as any)?.updatedMapping;
    if (updatedMapping) {
      // Update mapping with verified data
      setMapping(
        (prev) =>
          ({
            ...prev,
            ...updatedMapping,
          } as MappingDetailData)
      );
      setLoading(false);
      lastFetchedKey.current = key || null;
      setTenantId(tenantIdFromLabels(updatedMapping.labels));

      // Set default PDF hash from first PDF
      if (updatedMapping.pdfs?.[0]?.key) {
        setSelectedPdfHash(updatedMapping.pdfs[0].key);
      }

      // Clear the state so refresh works normally
      window.history.replaceState({}, document.title);
    } else if (key && lastFetchedKey.current !== key) {
      fetchMappingDetail();
      lastFetchedKey.current = key;
    }
  }, [key]); // Only depend on key, not location.state

  const fetchMappingDetail = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await mappingApi.get(key!);
      setMapping(response.data as any);

      // Set default PDF hash from first PDF
      const mappingData = response.data as any;
      setTenantId(tenantIdFromLabels(mappingData.labels));
      if (mappingData.pdfs?.[0]?.key) {
        setSelectedPdfHash(mappingData.pdfs[0].key);
      }
    } catch (err: any) {
      console.error('Failed to fetch mapping:', err);
      setError(err.response?.data?.message || 'Failed to load mapping details');
    } finally {
      setLoading(false);
    }
  };

  // const handleDelete = async () => {
  //   try {
  //     await deleteMapping(key!);
  //     success('Deleted', `Mapping "${mapping?.name}" has been deleted`);
  //     navigate('/mappings');
  //   } catch (err) {
  //     showError('Delete Failed', 'Failed to delete mapping');
  //   }
  // };

  const handleTest = () => {
    setTestModalOpen(true);
  };

  // Define constants and memoized values before conditional returns
  const statusColors: Record<string, string> = {
    NEW: 'default',
    AUTO_MAPPING: 'processing',
    IN_PROGRESS: 'warning',
    COMPLETED: 'success',
    IN_VERIFICATION: 'purple',
    VERIFIED: 'cyan',
    Verified: 'cyan', // Handle case from API
  };

  const statusLabels: Record<string, string> = {
    NEW: 'New',
    AUTO_MAPPING: 'Auto Mapping',
    IN_PROGRESS: 'In Progress',
    COMPLETED: 'Completed',
    IN_VERIFICATION: 'In Verification',
    VERIFIED: 'Verified',
    Verified: 'Verified', // Handle case from API
  };

  const canVerify = mapping
    ? !['VERIFIED'].includes(mapping.status?.toUpperCase())
    : false;

  const verificationContent = useMemo(() => {
    if (!mapping?.pdfs || mapping.pdfs.length === 0) return null;

    if (mapping.pdfs.length > 1) {
      return (
        <Tabs
          activeKey={selectedPdfHash}
          onChange={setSelectedPdfHash}
          items={mapping.pdfs.map((pdf) => ({
            key: pdf.key,
            label: `PDF: ${pdf.key.substring(0, 8)}...`,
            children: (
              <MappingVerification
                mappingKey={key!}
                pdfHash={pdf.key}
                pdfUrl={pdf.url}
                docTypeId={mapping.docType || 'po'}
                tenantId={tenantId}
                editingEnabled={isMapper}
              />
            ),
          }))}
        />
      );
    }

    return (
      <MappingVerification
        mappingKey={key!}
        pdfHash={selectedPdfHash || mapping.pdfs[0].key}
        pdfUrl={mapping.pdfs[0].url}
        docTypeId={mapping.docType || 'po'}
        tenantId={tenantId}
        editingEnabled={isMapper}
      />
    );
  }, [mapping?.pdfs, mapping?.docType, selectedPdfHash, canVerify, key, isMapper, tenantId]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error || !mapping) {
    return (
      <div>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/mappings')}
          style={{ marginBottom: 16 }}
        >
          Back to Mappings
        </Button>
        <Alert
          message="Error"
          description={error || 'Mapping not found'}
          type="error"
          showIcon
        />
      </div>
    );
  }

  const tabItems = [
    {
      key: 'details',
      label: 'Mapping Details',
      children: (
        <>
          {/* Mapping Details Card */}
          <Card title="Mapping Details" style={{ marginBottom: 24 }}>
            <Descriptions column={2} bordered>
              <Descriptions.Item label="Key">{mapping.key}</Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag
                  color={statusColors[mapping.status] || 'default'}
                  icon={
                    mapping.status?.toUpperCase() === 'VERIFIED' ? (
                      <CheckCircleOutlined />
                    ) : undefined
                  }
                >
                  {statusLabels[mapping.status] || mapping.status}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Document Type">
                <Tag color="blue">
                  {mapping.docType?.toUpperCase() || 'Unknown'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Date Format">
                {mapping.dateFormat || 'Not specified'}
              </Descriptions.Item>
              <Descriptions.Item label="Labels" span={2}>
                {mapping.labels && mapping.labels.length > 0 ? (
                  mapping.labels.map((label, index) => (
                    <Tag key={index} color="blue">
                      {label}
                    </Tag>
                  ))
                ) : (
                  <Text type="secondary">No labels</Text>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Creator">
                {mapping.creator || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Created">
                {mapping.dateCreated
                  ? format(new Date(mapping.dateCreated), 'MMM dd, yyyy HH:mm')
                  : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Last Updated By">
                {mapping.lastUpdatedBy || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Last Updated">
                {mapping.lastUpdated
                  ? format(new Date(mapping.lastUpdated), 'MMM dd, yyyy HH:mm')
                  : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Tenant ID" span={2}>
                {mapping.tenantId || '-'}
              </Descriptions.Item>
              {mapping.pdfs && mapping.pdfs.length > 0 && (
                <Descriptions.Item label="PDF Hash(es)" span={2}>
                  {mapping.pdfs.map((pdf, index) => (
                    <Tag key={index} color="purple">
                      {pdf.key}
                    </Tag>
                  ))}
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>
        </>
      ),
    },
    {
      key: 'verification',
      label: 'Verification',
      children: (
        <>
          {!canVerify && (
            <Alert
              description="This mapping has been verified and is ready to use."
              type="success"
              showIcon
              icon={<CheckCircleOutlined />}
              style={{ marginBottom: 24 }}
            />
          )}
          {verificationContent}
        </>
      ),
    },
  ];

  return (
    <div>
      {/* Header */}
      <div
        style={{
          marginBottom: 24,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Space>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/mappings')}
          >
            Back
          </Button>
          <Title level={2} style={{ margin: 0 }}>
            {mapping.name}
          </Title>
        </Space>
        <Space>
          {canVerify && (
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              onClick={() => setActiveTab('verification')}
            >
              Verify
            </Button>
          )}
          {isMapper && (
            <Button icon={<FileTextOutlined />} onClick={handleTest}>
              Test
            </Button>
          )}
          {/* Delete — not yet implemented
          <Popconfirm
            title="Delete mapping"
            description={`Are you sure you want to delete "${mapping.name}"?`}
            onConfirm={handleDelete}
            okText="Yes"
            cancelText="No"
          >
            <Button danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
          */}
        </Space>
      </div>

      <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />

      {isMapper && mapping.pdfs && mapping.pdfs.length > 0 && (
        <TestPreviewModal
          mappingKey={key!}
          pdfHash={selectedPdfHash || mapping.pdfs[0].key}
          pdfName={selectedPdfHash || mapping.pdfs[0].key}
          tenantId={tenantId}
          open={testModalOpen}
          onClose={() => setTestModalOpen(false)}
        />
      )}
    </div>
  );
}
